export interface Organisation {
  id: string;
  name: string;
  deletionInProgress: boolean;
  tokenLimitByUserType: {
    Technical: {
      tokenLimit: number;
      tokenLimitPeriod: string;
    };
    Normal: {
      tokenLimit: number;
      tokenLimitPeriod: string;
    };
  };
  maxUsers: number;
  maxTechnicalUsers: number;
  maxGroups: number;
  maxIndices: number;
  maxFunctionCallDefinitions: number;
  maxApiKeys: number;
  maxModels: number;
  maxDocumentsPerIndex: number;
  maxStorageSizePerIndex: number;
  maxSubscriptions: number;
  maxApplicationsByType: {
    chat: number;
    composite: number;
    composite_e1: number;
    composite_e2: number;
    llm_pass_through: number;
  };
  logoUrl: string | null;
  featureGates: FeatureGate[];
  userVisibleCustomAttributes: { [key: string]: string };
  customAttributes: { [key: string]: string };
  applicationAssignableToDefault: UserType[];
}

type UserType = 'Normal' | 'Technical';

export interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  initialPassword?: string;
  external: boolean;
  roles: string[];
  type: UserType;
}

export interface Group {
  id: string;
  name: string;
  description: string;
  roles: string[];
  attributes: { [key: string]: string };
}

export interface AdminRole {
  id: string;
  name: string;
}

export interface Application {
  id: string;
  name: string;
  displayName: string;
  description: string;
  missionStatement: string;
  documentRetrievalCount: number;
  finalizeInstructions: string;
  systemPromptTemplate: string;
  userPromptTemplate: string;
  visibilityType: VisibilityType;
  applicationType: ApplicationType;
  completionModels: string[];
  modelError: boolean;
  debugSettings: Partial<DebugSetting>[];
  indices: ApplicationIndex[] | undefined;
  validationIndices: string[] | undefined;
  functionCallDefinitions: string[] | undefined;
  initialMessage: string;
}

export interface ApplicationIndex {
  id: string;
  name: string;
  systemPromptTemplate: string;
  userPromptTemplate: string;
  retrievalQueryCustomizationScript: string;
  fieldsIncludedInSources: string[];
  fieldsAvailableForQuery: string[];
  metadataFields: AdditionalMetadata[];
  modelError: boolean;
}

export interface FunctionCallDefinition {
  id: string;
  name: string;
  displayName: string;
  description: string;
  parameterSpecificationType: ParameterSpecificationType;
  parameterSpecification: Record<string, unknown>;
}

export interface Index {
  id: string;
  name: string;
  displayName: string;
  description: string;
  type: IndexType;
  chunkSize: number;
  subscriptionName: string;
  active: boolean;
  additionalMetadataFields: AdditionalMetadata[];
  modelIds: string[];
  modelError: boolean;
}

export interface Usage {
  applicationId: string;
  applicationName: string;
  applicationDisplayName: string;
  applicationDescription: string;
  yearMonth: string;
  usedTokens: number;
}

export interface Subscription {
  type: SubscriptionType;
  id: string;
  name: string;
  active: boolean;
  inactiveAt: string | null;
  billingProvider: SubscriptionBillingProvider;
  billingProviderState: SubscriptionBillingProviderState | null;
  allowedActions: SubscriptionAllowedActions[];
  trial: boolean;
  privilegedWriteOnly: boolean;
  seatsTotal?: Seats;
  seatsUsed?: Seats;
  desiredTotalSeats?: Seats;
  assignableTo?: UserType;
  usages?: Usage[];
}

type SubscriptionBillingProvider = 'STRIPE' | 'NONE';
type SubscriptionBillingProviderState = 'UNPAID' | 'OVERDUE';
type SubscriptionAllowedActions =
  | 'EDIT'
  | 'DELETE'
  | 'BILLING_PORTAL'
  | 'CHECKOUT';

export interface Seats {
  user: number;
}

export interface NewIndexDocument {
  file: File;
  title: string;
  date: string;
  filename: string;
  needsAnonymization: boolean;
  ocr: boolean;
  additionalMetadata: string;
}

export interface IndexSchema {
  availableFieldDefinitions: IndexSchemaFieldDefinition[];
  availableFieldTypes: IndexSchemaMetadataType[];
  availableDebugSettings: DebugSetting[];
}

export interface IndexSchemaFieldDefinition {
  id: string;
  displayName: string;
  type: IndexSchemaMetadataType;
}

export interface ApplicationSchema {
  availableDebugSettings: DebugSetting[];
  applicationTypes: ApplicationType[];
}

export interface DebugSetting {
  id: string;
  displayName: string;
  description: string;
}

export interface AdditionalMetadata {
  id: string;
  displayName: string;
  includedInSources: boolean;
  optional: boolean;
  type: string;
  ptc: boolean;
}

export interface OrganisationSchemaOrgAdmin {
  featureGates: FeatureGate[];
  organisationLimits: OrganisationLimit[];
  tokenLimitPeriodChoices?: { name: string; value: string }[];
}

export interface FeatureGate {
  id: string;
  description: string;
}

export interface OrganisationLimit {
  id: string;
  name: string;
  maxValue: number;
}

export interface OrganisationSchema {
  maxUsers: number;
  maxGroups: number;
  maxIndices: number;
  maxFunctionCallDefinitions: number;
  maxApplicationsByType: {
    chat: number;
    composite: number;
    composite_e1: number;
    composite_e2: number;
    llm_pass_through: number;
  };
  maxApiKeys: number;
  maxModels: number;
  maxDocumentsPerIndex: number;
  maxStorageSizePerIndex: number;
  maxSubscriptions: number;
}

export interface ApiKey {
  id: string;
  displayName: string;
  type: ModelEnum;
  value: string;
  scopes: ModelScope[];
  privilegedWriteOnly: boolean;
}

export interface Model {
  id: string;
  scope: ModelScope;
  displayName: string;
  type: ModelEnum;
  tokenLimit: number;
  parameters: Record<string, unknown>;
  apiKeyId: string;
  status: ModelStatus;
  errorMessage: string;
  privilegedWriteOnly: boolean;
}

type ModelScope = 'embedding' | 'completion';

export interface RawModelType {
  type: ModelEnum;
  scopes: ModelScope[];
  schema: Record<string, unknown>;
}

export interface ModelType {
  type: ModelEnum;
  scopes: ModelTypeScope[];
}

export interface ModelTypeScope {
  scope: ModelScope;
  schema: Record<string, unknown>;
}

export interface AggregatedRating {
  histogram: { [key: string]: number };
  averages: { [key: string]: number };
}

export interface Feedback {
  id: number;
  modelType: string;
  feedbackDate: Date;
  feedbackScore: number;
  feedbackText: string;
  promptResult: string;
  userInput: string;
  sources: FeedbackSource[];
}

export interface FeedbackSource {
  content: string;
  metadata: FeedbackSourceMetadata;
  documentReference: FeedbackSourceDocumentReference;
}

export interface FeedbackSourceMetadata {
  filename: string;
  author: string;
  title: string;
  referenceId: string;
}

export interface FeedbackSourceDocumentReference {
  bucket: string;
  path: string;
  id: string;
}

export interface IndexDocument {
  filename: string;
  title: string;
  date: number;
  [metadata: string]: string | number;
}

export interface PagedResult {
  content: unknown[];
  totalPages: number;
  totalElements: number;
}

export enum PermissionScope {
  VIEW = 'VIEW',
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  ASSIGN_ADMIN = 'ASSIGN_ADMIN',
  ASSIGN_INDEX = 'ASSIGN_INDEX',
  ASSIGN_USER = 'ASSIGN_USER',
  CHECKOUT = 'CHECKOUT',
  MANAGE_DOCS = 'MANAGE_DOCS',
  REDUCED_VIEW = 'REDUCED_VIEW',
  FEEDBACK = 'FEEDBACK',
  MANAGE_APPS = 'MANAGE_APPS',
}

export enum VisibilityType {
  SHARED = 'shared',
  PRIVATE = 'private',
}

export enum ApplicationType {
  CHAT = 'chat',
  COMPOSITE = 'composite',
  E1 = 'composite_e1',
  E2 = 'composite_e2',
  LLM_PASS_THROUGH = 'llm_pass_through',
}

/**
 * Determines which fields should be enabled/disabled based on the backend validation logic
 * @param applicationType The selected application type
 * @returns Object with field names as keys and boolean values indicating if the field should be enabled
 */
export function getFieldAvailabilityForApplicationType(
  applicationType: ApplicationType,
): {
  [key: string]: boolean;
} {
  switch (applicationType) {
    case ApplicationType.LLM_PASS_THROUGH:
      return {
        missionStatement: false,
        finalizeInstructions: false,
        functions: false,
        indices: false,
        validationIndices: false,
        indexFieldsAvailableForQuery: false,
        documentRetrievalCount: false,
      };
    case ApplicationType.CHAT:
      return {
        missionStatement: false,
        finalizeInstructions: false,
        functions: false,
        indices: true,
        validationIndices: false,
        indexFieldsAvailableForQuery: false,
        documentRetrievalCount: true,
      };
    case ApplicationType.COMPOSITE:
      return {
        missionStatement: true,
        finalizeInstructions: true,
        functions: true,
        indices: true,
        validationIndices: true,
        indexFieldsAvailableForQuery: true,
        documentRetrievalCount: true,
      };
    case ApplicationType.E1:
      return {
        missionStatement: true,
        finalizeInstructions: true,
        functions: false,
        indices: false,
        validationIndices: false,
        indexFieldsAvailableForQuery: false,
        documentRetrievalCount: false,
      };
    case ApplicationType.E2:
      return {
        missionStatement: true,
        finalizeInstructions: true,
        functions: true,
        indices: true,
        validationIndices: true,
        indexFieldsAvailableForQuery: true,
        documentRetrievalCount: true,
      };
    default:
      return {
        missionStatement: false,
        finalizeInstructions: false,
        functions: false,
        indices: false,
        validationIndices: false,
        indexFieldsAvailableForQuery: false,
        documentRetrievalCount: false,
      };
  }
}

export enum IndexType {
  VALIDATION = 'VALIDATION',
  KNOWLEDGE_STORE = 'KNOWLEDGE_STORE',
}

export enum SubscriptionType {
  SEAT = 'Seat',
  USAGE = 'Usage',
}

export enum ResourceType {
  SUBSCRIPTIONS = 'subs',
  SUBSCRIPTION = 'sub',
  APPLICATION = 'application',
  APPLICATIONS = 'applications',
  INDEX = 'index',
  INDICES = 'indices',
  USERS = 'users',
  API_KEYS = 'api-keys',
  MODELS = 'models',
  GROUPS = 'groups',
  FUNCTION_CALL_DEFINITIONS = 'function-call-definitions',
}

export enum IndexSchemaMetadataType {
  AUTHOR = 'author',
  KEYWORDS = 'keywords',
  DATE = 'date',
  STRING = 'string',
}

export enum FeatureGateEnum {
  PROCESS_TYPE_COMPOSITE = 'processTypeComposite',
  USER_PROMPT_TEMPLATE = 'userPromptTemplate',
  EMBEDDINGS = 'embeddings',
  USER_IMPORT = 'userImport',
  CUSTOMIZABLE_RETRIEVAL_QUERIES = 'customizableRetrievalQueries',
}

export enum ModelEnum {
  OPEN_AI = 'OpenAI',
  OPEN_AI_COMPATIBLE = 'OpenAICompatible',
  AZURE_OPEN_AI = 'AzureOpenAI',
  MISTRAL = 'Mistral',
  IONOS = 'Ionos',
}

export enum ModelStatus {
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  READY = 'READY',
}

export enum ParameterSpecificationType {
  OPENAI_JSON_SCHEMA = 'openAIJsonSchema',
}

interface SubscriptionPrice {
  amount: number;
  currency: string;
  vatRate: number;
  taxBehavior: string;
  usageType: string;
}

interface SubscriptionSettings {
  activeOnCreation: boolean;
  seatEnabledForUserTypes: UserType[];
  trialEnabled: boolean;
  usageEnabledForApplicationTypes: ApplicationType[];
}

export interface SubscriptionSchema {
  settings: SubscriptionSettings;
  seatPrice: SubscriptionPrice;
  reasoningPrice: SubscriptionPrice;
}

export interface UploadUsage {
  totalCount: number;
  totalFileSize: number;
}

export interface MaintenanceStatus {
  maintenanceEnabled: boolean;
}
